import { useState, useEffect } from 'react'
import { FileText, X, AlertCircle, Loader2, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { documentService } from '@/services'
import type { Document as ApiDocument, StructuredBusinessContext } from '@/types/api'
import type { ExplorerDoc } from './types'

interface DocumentDetailProps {
  doc: ExplorerDoc
  onClose: () => void
  startupId?: string
}

/** Category label map for human-friendly display. */
const CATEGORY_LABELS: Record<string, string> = {
  fundraising_finance: 'Fundraising & Finance',
  customer_intelligence: 'Customer Intelligence',
  product_engineering: 'Product & Engineering',
  go_to_market: 'Go-to-Market',
  legal_compliance: 'Legal & Compliance',
  team_ops: 'Team & Ops',
  unsorted: 'Unsorted',
}

export function DocumentDetail({ doc, onClose, startupId }: DocumentDetailProps) {
  const [activeTab, setActiveTab] = useState('intelligence')
  const [fullDoc, setFullDoc] = useState<ApiDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!startupId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    documentService
      .get(startupId, doc.id)
      .then((d) => setFullDoc(d))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load document'))
      .finally(() => setLoading(false))
  }, [startupId, doc.id])

  const extracted = fullDoc?.extractedData
  const category = extracted?.category ?? doc.category
  const categoryLabel = category ? CATEGORY_LABELS[category] ?? category : null
  const summary = extracted?.summary
  const topics = extracted?.topics ?? []
  const structured = extracted?.structuredContext
  const markdownPreview = extracted?.markdownPreview
  const rawText = extracted?.text
  const confidence = extracted?.confidence
  const subCategory = extracted?.subCategory
  const hasAnyData = !!summary || topics.length > 0 || !!structured || !!markdownPreview || !!rawText

  // Debug: log extracted data shape on mount
  useEffect(() => {
    if (fullDoc) {
      console.log('[DocumentDetail] extractedData keys:', Object.keys(extracted ?? {}))
      console.log('[DocumentDetail] summary:', summary ? `${summary.length} chars` : null)
      console.log('[DocumentDetail] topics:', topics)
      console.log('[DocumentDetail] structuredContext:', structured ? Object.keys(structured) : null)
      console.log('[DocumentDetail] markdownPreview:', markdownPreview ? `${markdownPreview.length} chars` : null)
      console.log('[DocumentDetail] text:', rawText ? `${rawText.length} chars` : null)
      console.log('[DocumentDetail] category:', category, 'confidence:', confidence)
      console.log('[DocumentDetail] fullDoc keys:', Object.keys(fullDoc))
    } else if (startupId) {
      console.log('[DocumentDetail] No fullDoc loaded (loading/error state)')
    } else {
      console.log('[DocumentDetail] No startupId — using ExplorerDoc fallback data')
      console.log('[DocumentDetail] ExplorerDoc keys:', Object.keys(doc))
      console.log('[DocumentDetail] ExplorerDoc.category:', doc.category)
      console.log('[DocumentDetail] ExplorerDoc.subCategory:', doc.subCategory)
      console.log('[DocumentDetail] ExplorerDoc.confidence:', doc.confidence)
    }
  }, [fullDoc, extracted, summary, topics, structured, markdownPreview, rawText, category, confidence, subCategory, startupId, doc])

  return (
    <div className="h-full flex flex-col bg-background border-l border-border/50 shadow-2xl animate-in slide-in-from-right duration-300 w-full lg:w-[600px] xl:w-[800px] absolute right-0 top-0 z-20">
      {/* Header */}
      <div className="h-14 border-b border-border/50 flex items-center justify-between px-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <FileText className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-medium text-sm leading-none">{fullDoc?.name ?? doc.title}</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {fullDoc?.processedAt ? `Processed ${new Date(fullDoc.processedAt).toLocaleDateString()}` : 'Processing…'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Tabs */}
      <div className="lg:hidden border-b border-border/50 px-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full h-10 bg-transparent p-0">
            <TabsTrigger value="intelligence" className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent">
              Intelligence
            </TabsTrigger>
            <TabsTrigger value="source" className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent">
              Source
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="text-destructive text-sm">{error}</div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <div className="flex-1 flex overflow-hidden">
          {/* Source Pane */}
          <SourcePane
            extracted={extracted}
            activeTab={activeTab}
            markdownPreview={markdownPreview}
            rawText={rawText}
          />

          {/* Intelligence Pane */}
          <IntelligencePane
            summary={summary}
            topics={topics}
            categoryLabel={categoryLabel}
            subCategory={subCategory}
            confidence={confidence}
            structured={structured}
            activeTab={activeTab}
          />
        </div>
      )}

      {/* Empty state when no extracted data */}
      {!loading && !error && !hasAnyData && (
        <div className="flex-1 flex items-center justify-center p-8 text-center">
          <div className="max-w-sm">
            <FileText className="h-12 w-12 mx-auto mb-4 text-text-tertiary" />
            <p className="text-sm font-medium text-text-primary mb-1">No intelligence extracted yet</p>
            <p className="text-xs text-text-tertiary">
              The document may still be processing, or no structured data was found.
            </p>
            <div className="mt-4 p-3 bg-bg-elevated rounded-lg border border-border text-left">
              <p className="text-xs text-text-tertiary font-medium mb-2">Debug info:</p>
              <p className="text-xs text-text-secondary font-mono break-all">
                extractedData: {fullDoc ? (extracted ? 'present' : 'undefined') : 'no doc'}
              </p>
              <p className="text-xs text-text-secondary font-mono break-all mt-1">
                fullDoc keys: {fullDoc ? Object.keys(fullDoc).join(', ') : '—'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Icon name → emoji map ─────────────────────────────────── */

const ICON_EMOJI: Record<string, string> = {
  flag: '🚩',
  star: '⭐',
  heart: '❤️',
  fire: '🔥',
  bolt: '⚡',
  target: '🎯',
  trophy: '🏆',
  chart: '📊',
  globe: '🌍',
  lightbulb: '💡',
  key: '🔑',
  shield: '🛡️',
  clock: '⏰',
  check: '✅',
  x: '❌',
  warning: '⚠️',
  info: 'ℹ️',
  mail: '📧',
  phone: '📞',
  map: '🗺️',
  user: '👤',
  users: '👥',
  calendar: '📅',
  bell: '🔔',
  search: '🔍',
  settings: '⚙️',
  lock: '🔒',
  unlock: '🔓',
  eye: '👁️',
  eyeOff: '🙈',
  camera: '📷',
  image: '🖼️',
  video: '🎥',
  music: '🎵',
  mic: '🎤',
  book: '📖',
  bookmark: '🔖',
  paperclip: '📎',
  link: '🔗',
  download: '⬇️',
  upload: '⬆️',
  cloud: '☁️',
  database: '🗄️',
  code: '💻',
  terminal: '💻',
  bug: '🐛',
  rocket: '🚀',
  gift: '🎁',
  crown: '👑',
  diamond: '💎',
  sparkle: '✨',
  party: '🎉',
  smile: '😊',
  thumbsup: '👍',
  thumbsdown: '👎',
  pencil: '✏️',
  trash: '🗑️',
  edit: '✏️',
  plus: '➕',
  minus: '➖',
  arrowUp: '⬆️',
  arrowDown: '⬇️',
  arrowLeft: '⬅️',
  arrowRight: '➡️',
}

/** Convert [icon-xxx] placeholders to emoji. */
function renderIconPlaceholder(md: string): string {
  return md.replace(/\[icon-([a-zA-Z0-9_-]+)\]/g, (_match: string, name: string) => {
    const key = name.toLowerCase()
    return ICON_EMOJI[key] ?? `[${name}]`
  })
}

/* ── Lightweight markdown → HTML converter ──────────────────── */

function renderMarkdown(md: string): string {
  return renderIconPlaceholder(md)
    // Headers (## then #)
    .replace(/^## (.+)$/gm, '<h2 class="text-base font-semibold text-text-primary mt-6 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-lg font-semibold text-text-primary mt-6 mb-2">$1</h1>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-text-primary">$1</strong>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-bg-elevated rounded text-sm font-mono text-text-primary">$1</code>')
    // Bullet lists
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-text-primary">$1</li>')
    // Wrap consecutive <li> in <ul>
    .replace(/((?:<li[^>]*>.*<\/li>\n?)+)/g, '<ul class="my-2 space-y-1">$1</ul>')
    // Paragraphs (lines not already wrapped)
    .replace(/^(?!<[uhli]|<strong)(.+)$/gm, '<p class="text-sm text-text-primary leading-relaxed my-1">$1</p>')
    // Clean up trailing newlines from block elements
    .replace(/<\/(h[12]|ul|p)>\n/g, '</$1>')
}

/* ── Source Pane ─────────────────────────────────────────────── */

function SourcePane({
  extracted,
  activeTab,
  markdownPreview,
  rawText,
}: {
  extracted?: ApiDocument['extractedData']
  activeTab: string
  markdownPreview?: string
  rawText?: string
}) {
  const [showPreview, setShowPreview] = useState(!!markdownPreview)
  const hasMarkdown = !!markdownPreview
  const hasText = !!rawText || !!extracted?.text
  const content = showPreview && hasMarkdown ? markdownPreview : (hasText ? rawText ?? extracted?.text : null)
  const isMarkdown = showPreview && hasMarkdown

  return (
    <div className={cn(
      'flex-1 bg-secondary/20 p-6 overflow-auto border-r border-border/50 lg:block',
      activeTab === 'source' ? 'block' : 'hidden lg:block'
    )}>
      <div className="max-w-prose mx-auto space-y-4">
        {/* Toggle between preview and raw text */}
        {hasMarkdown && hasText && (
          <div className="flex items-center justify-end gap-2">
            <span className={cn('text-xs', showPreview ? 'text-text-primary font-medium' : 'text-text-tertiary')}>Preview</span>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className={cn(
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                showPreview ? 'bg-primary' : 'bg-bg-elevated'
              )}
            >
              <span className={cn(
                'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                showPreview ? 'translate-x-4' : 'translate-x-1'
              )} />
            </button>
            <span className={cn('text-xs', !showPreview ? 'text-text-primary font-medium' : 'text-text-tertiary')}>Raw</span>
          </div>
        )}

        {content ? (
          isMarkdown ? (
            <div
              className="w-full bg-bg-primary rounded-lg border border-border p-6 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
            />
          ) : (
            <div className="w-full bg-bg-primary rounded-lg border border-border p-6 text-sm font-mono leading-relaxed whitespace-pre-wrap text-text-primary">
              {content}
            </div>
          )
        ) : (
          <div className="w-full h-[400px] bg-secondary/30 rounded flex items-center justify-center text-muted-foreground text-sm">
            No preview available
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Intelligence Pane ──────────────────────────────────────── */

interface IntelligencePaneProps {
  summary?: string
  topics: string[]
  categoryLabel: string | null
  subCategory?: string
  confidence?: number
   structured?: StructuredBusinessContext
  activeTab: string
}

/** Human-readable section labels for structured context. */
const SECTION_LABELS: Record<string, string> = {
  mission: 'Mission & Vision',
  targetMarket: 'Target Market',
  businessModel: 'Business Model',
  product: 'Product',
  goToMarket: 'Go-to-Market',
  competition: 'Competition',
  team: 'Team',
  fundraising: 'Fundraising',
}

interface StructuredField {
  label: string
  value: string | string[] | null
}

function getSectionState(
  section: Record<string, unknown> | undefined
): 'empty' | 'partial' | 'full' {
  if (!section) return 'empty'
  const values = Object.values(section)
  const hasData = values.some(
    (v) => v !== null && v !== undefined && v !== ''
  )
  if (!hasData) return 'empty'
  const filled = values.filter(
    (v) => v !== null && v !== undefined && v !== ''
  ).length
  return filled === values.length ? 'full' : 'partial'
}

function getSectionFields(
  section: Record<string, unknown> | undefined
): StructuredField[] {
  if (!section) return []
  return Object.entries(section)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => ({
      label: key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase()),
      value: Array.isArray(value) ? value : value ?? null,
    })) as StructuredField[]
}

function allSectionsEmpty(
  structured: Record<string, unknown> | undefined
): boolean {
  if (!structured) return true
  return Object.values(SECTION_LABELS).every((_, i) => {
    const key = Object.keys(SECTION_LABELS)[i]
    const section = structured[key as keyof typeof SECTION_LABELS] as Record<string, unknown> | undefined
    return getSectionState(section) === 'empty'
  })
}

function StructuredSection({ label, state, fields }: { label: string; state: 'empty' | 'partial' | 'full'; fields: StructuredField[] }) {
  const [expanded, setExpanded] = useState(state !== 'empty')

  if (state === 'empty') {
    return (
      <div className="rounded-lg border border-border bg-bg-elevated px-4 py-3 flex items-center justify-between">
        <span className="text-sm text-text-tertiary">{label}</span>
        <span className="text-xs text-text-tertiary">No data</span>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-bg-elevated overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-bg-surface-hover"
      >
        <span className="text-sm font-medium text-text-primary">{label}</span>
        <span className="text-xs text-text-tertiary">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {fields.map((field) => {
            const values = Array.isArray(field.value) ? field.value : null
            if (values && values.length > 0) {
              return (
                <div key={field.label}>
                  <span className="text-xs text-text-tertiary">{field.label}</span>
                  <ul className="mt-1 space-y-1">
                    {values.map((v, i) => (
                      <li key={i} className="text-sm text-text-primary leading-relaxed">{v}</li>
                    ))}
                  </ul>
                  <Separator className="my-3" />
                </div>
              )
            }
            if (field.value != null && field.value !== '') {
              return (
                <div key={field.label}>
                  <span className="text-xs text-text-tertiary">{field.label}</span>
                  <p className="mt-1 text-sm text-text-primary leading-relaxed">{String(field.value)}</p>
                  <Separator className="my-3" />
                </div>
              )
            }
            return (
              <div key={field.label} className="flex items-center gap-2">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-tertiary italic">Not available</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function IntelligencePane({ summary, topics, categoryLabel, subCategory, confidence, structured, activeTab }: IntelligencePaneProps) {
  const structuredObj = structured as Record<string, unknown> | undefined
  const hasStructured = structuredObj && !allSectionsEmpty(structuredObj)

  return (
    <div className={cn('flex-1 bg-background p-6 overflow-auto lg:block', activeTab === 'intelligence' ? 'block' : 'hidden lg:block')}>
      <div className="space-y-6">
        {/* Category badges */}
        {categoryLabel && (
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
              <Tag className="h-3 w-3" /> {categoryLabel}
            </span>
            {subCategory && (
              <span className="text-xs bg-secondary px-2 py-1 rounded-full text-muted-foreground">{subCategory}</span>
            )}
            {confidence != null && (
              <span className="text-xs bg-secondary px-2 py-1 rounded-full text-muted-foreground">{Math.round(confidence * 100)}% confidence</span>
            )}
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Executive Summary</h4>
            <div className="rounded-lg border border-border bg-bg-elevated p-5">
              <p className="text-sm leading-relaxed text-text-primary">{summary}</p>
            </div>
          </div>
        )}

        {/* Topics */}
        {topics.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Topics</h4>
              <div className="flex flex-wrap gap-2">
                {topics.map((topic) => (
                  <span key={topic} className="text-xs px-2.5 py-1 rounded-full bg-bg-surface text-text-secondary border border-border">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Structured Context */}
        {hasStructured && (
          <>
            <Separator />
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Structured Context</h4>
              <div className="space-y-2">
                {Object.entries(SECTION_LABELS).map(([key, label]) => {
                  const section = structuredObj?.[key as keyof typeof SECTION_LABELS] as Record<string, unknown> | undefined
                  const state = getSectionState(section)
                  const fields = getSectionFields(section)
                  // Skip sections with no non-empty fields
                  if (fields.length === 0) return null
                  return (
                    <StructuredSection key={key} label={label} state={state} fields={fields} />
                  )
                }).filter(Boolean)}
              </div>
            </div>
          </>
        )}

        {/* No data state */}
        {!categoryLabel && !summary && topics.length === 0 && !hasStructured && (
          <div className="flex gap-2 items-start p-3 bg-accent-warning/10 rounded-lg text-sm text-accent-warning">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>No intelligence extracted yet. The document may still be processing.</span>
          </div>
        )}
      </div>
    </div>
  )
}
